import { useCallback } from "react";
import TagManager from "react-gtm-module";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import * as GQL from "src/types/graphql";
import { deleteUTMCookie, getUTMParamsFromCookie } from "src/utils/utmTracking";
import { INSERT_PERSON_CONVERSION } from "./mutations";

/**
 * Conversion types that can be tracked in the person_conversion_attribution table
 */
export enum ConversionType {
  ACCOUNT_CREATED = "account-created",
}

/**
 * Hook to track person conversions with UTM parameters
 *
 * This hook provides a function to save UTM tracking data to the person_conversion_attribution table.
 * It automatically reads UTM parameters from the organization-specific cookie set during page load.
 * Conversions are only tracked when the cookie's organization matches the person's organization.
 * After successful tracking, the cookie is deleted to ensure one-time use.
 *
 * @example
 * ```tsx
 * const { trackConversion } = usePersonConversion();
 *
 * // After account creation
 * await trackConversion(personId, 'philasd', ConversionType.ACCOUNT_CREATED);
 * ```
 */
export function usePersonConversion() {
  const [insertConversion, { remoteData }] = useRemoteDataMutation<
    GQL.InsertPersonConversion,
    GQL.InsertPersonConversionVariables
  >(INSERT_PERSON_CONVERSION);

  /**
   * Tracks a conversion for a person by sending event to Google Tag Manager
   * and optionally inserting UTM data into person_conversion_attribution table.
   *
   * GTM event is always sent. Database insert only happens if:
   * 1. UTM parameters exist in the cookie for this organization
   * 2. The organization matches (prevents cross-organization attribution)
   *
   * After successful database insert, the cookie is deleted to ensure UTM params are only used once.
   *
   * @param personId - The UUID of the person who converted
   * @param organizationPath - The organization path (e.g., 'philasd') to match against the cookie
   * @param conversionType - The type of conversion being tracked (e.g., ConversionType.ACCOUNT_CREATED)
   * @returns Promise that resolves when the conversion is tracked
   */
  const trackConversion = useCallback(
    async (
      personId: string,
      organizationPath: string,
      conversionType: ConversionType
    ) => {
      const utmParams = getUTMParamsFromCookie(organizationPath);

      // Always send event to Google Tag Manager
      TagManager.dataLayer({
        dataLayer: {
          event: conversionType,
          organization_path: organizationPath,
        },
      });

      // Only insert into database if we have UTM parameters for this organization
      if (!utmParams) {
        console.debug(
          "No UTM parameters found for organization, skipping database insert but GTM event sent",
          { organizationPath, conversionType }
        );
        return;
      }

      try {
        await insertConversion({
          variables: {
            person_id: personId,
            utm_source: utmParams.utm_source ?? null,
            utm_medium: utmParams.utm_medium ?? null,
            utm_campaign: utmParams.utm_campaign ?? null,
            utm_term: utmParams.utm_term ?? null,
            utm_content: utmParams.utm_content ?? null,
            utm_custom: utmParams.utm_custom ?? null,
            conversion_type: conversionType,
          },
        });

        // Delete the cookie after successful tracking to ensure one-time use
        deleteUTMCookie(organizationPath);

        console.debug(
          "Person conversion tracked successfully and cookie deleted",
          {
            personId,
            organizationPath,
            conversionType,
            ...utmParams,
          }
        );
      } catch (error) {
        // Log error but don't throw - conversion tracking shouldn't break the flow
        console.error("Failed to track person conversion:", error);
      }
    },
    [insertConversion]
  );

  return {
    trackConversion,
    isTracking: remoteData.isLoading(),
    remoteData,
  };
}
